import React, { Fragment, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Card, CardBody, CardFooter, CardHeader, Col, FormGroup, Input, Label, Row } from "reactstrap";
import MultiSearchPanel from "../Search/MultiSearchPanel";
import { useService } from "../../context/ServiceContext";
import { useDropzone } from "react-dropzone";
import LoadedContent from "../LoadedContent";
import { fireAlert } from "../../utils/alerts";
import ErrorAlertContent from "../ErrorAlertContent";
import { MultiSelect } from "react-multi-select-component";

function CertificatePdfUpload({ history }) {
  const intl = useIntl();

  const { certificatePdfStore } = useService();
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileDetails, setFileDetails] = useState({});
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const keys = Object.keys(fileDetails);
    let valid = true;
    for (let i = 0; i < keys.length; i++) {
      const details = fileDetails[keys[i]];
      if (details.isValid == false) valid = false;
    }
    setIsValid(valid);
  }, [fileDetails]);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    },
  });

  // const removeFile = (fileToRemove) => {
  //   setFiles((prevFiles) => prevFiles.filter((file) => file.path !== fileToRemove.path));
  // };

  const removeFile = (file) => {
    setFiles(files.filter((f) => f.path !== file.path));
    // Also remove the file's details from fileDetails
    const newFileDetails = { ...fileDetails };
    delete newFileDetails[file.path];
    setFileDetails(newFileDetails);
  };

  async function save() {
    setIsLoading(true);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("Files", file);
      });

      formData.append("FileDetails", JSON.stringify(fileDetails["NA"]));
      await certificatePdfStore.store(formData, { "Content-Type": "multipart/form-data" });

      fireAlert(
        intl.formatMessage({ id: "certificates.successfully_uploaded_certificate", defaultMessage: "Successfully uploaded certificate(s)" }),
        intl.formatMessage({ id: "app.success", defaultMessage: "Success" }),
        "success",
      ).then(() => {
        history.push(`/certificates`);
      });
    } catch (error) {
      if (error.response.status == 400) {
        fireAlert("", error.response.data, "info");
      } else {
        fireAlert(
          <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
          intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
          "error",
        );
      }
    }

    setIsLoading(false);
  }

  // async function uploadCertificate(dataPackage) {
  //   const formData = new FormData();
  //   dataPackage.files.forEach((file) => {
  //     formData.append("Files", file);
  //   });

  //   formData.append("FileDetails", JSON.stringify(fileDetails));
  //   await certificatePdfStore.store(formData, { "Content-Type": "multipart/form-data" });
  // }

  const updateFileDetails = (filePath, field, value) => {
    setFileDetails((prev) => ({
      ...prev,
      [filePath]: {
        ...prev[filePath],
        [field]: value,
      },
    }));
  };

  return (
    <>
      <Row>
        <Col>
          <Card className="mt-3">
            <CardHeader>Upload Certificates</CardHeader>
            <CardBody>
              <Row>
                <Col>
                  <Row>
                    <Col>
                      {files.length > 0 && (
                        <>
                          <h3 className="card-title">
                            <FormattedMessage id="certificates.files_to_upload" defaultMessage="Files to upload" />
                          </h3>
                          {/* {files.map((f, i) => (
                            <CertificateUploadDetails
                              key={f.path}
                              fileIndex={i}
                              file={f}
                              removeFile={removeFile}
                              updateFileDetails={updateFileDetails}
                              details={fileDetails[f.path] || {}}
                            />
                          ))} */}
                          <CertificateUploadDetails files={files} removeFile={removeFile} updateFileDetails={updateFileDetails} details={fileDetails["NA"] || {}} />
                        </>
                      )}
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <button type="button" onClick={open} className="btn btn-outline-dark">
                          <FormattedMessage id="app.add" defaultMessage="Add" />
                        </button>
                        <button type="button" disabled={files.length == 0 || isValid == false || isLoading} onClick={save} className="btn btn-primary">
                          <FormattedMessage id="app.upload" defaultMessage="Upload" />
                        </button>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}

function CertificateUploadDetails({ files, removeFile, updateFileDetails, details }) {
  const intl = useIntl();
  const { certificateSearchProvider, certificateLanguagesProvider } = useService();

  const [languages, setLanguages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [existingSeries, setExistingSeries] = useState([]);
  const [selectedMultiSeries, setSelectedMultiSeries] = useState([]);

  const convertDay = (date) => {
    const today = date ?? new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const selectedCompany = details.selectedCompany ?? -1;
  const selectedSeries = details.selectedSeries;
  const selectedLanguage = details.selectedLanguage ?? "";
  const name = details.name ?? "";
  const startDate = details.startDate ?? convertDay();
  const endDate = details.endDate ?? convertDay();
  const noEndDate = details.noEndDate ?? true;

  useEffect(() => {
    const isEndDateValid = noEndDate || new Date(endDate) >= new Date();
    updateFileDetails("NA", "isValid", name !== "" && selectedMultiSeries?.length > 0 && selectedLanguage !== "" && isEndDateValid);
  }, [selectedMultiSeries, selectedLanguage, endDate, noEndDate, name]);

  useEffect(() => {
    async function getData() {
      try {
        const languages = await certificateLanguagesProvider.get();
        setLanguages(languages ?? []);
      } catch (error) {}
    }
    if (languages.length === 0) getData();
  }, []);

  useEffect(() => {
    async function getData() {
      try {
        const searchResults = await certificateSearchProvider.get({
          keyCompany: selectedCompany,
        });
        setExistingSeries(
          (searchResults?.series ?? []).map((a) => {
            return {
              label: a.value,
              value: a.key,
            };
          }) ?? [],
        );
      } catch (error) {}
    }
    getData();
  }, [selectedCompany]);

  useEffect(() => {
    async function getData() {
      try {
        const searchResults = await certificateSearchProvider.get({
          series: selectedSeries !== undefined && selectedSeries.length > 0 ? selectedSeries[0] : "",
        });
        setCompanies(searchResults?.companies ?? []);
      } catch (error) {}
    }
    getData();
  }, [selectedSeries]);

  useEffect(() => {
    if (selectedMultiSeries?.length > 0) {
      updateFileDetails(
        "NA",
        "selectedSeries",
        selectedMultiSeries.map((s) => {
          return {
            shortNameComercial: s.label,
            codSerieComercial: s.value,
          };
        }),
      );
    }
  }, [selectedMultiSeries]);

  const customValueRenderer = (selected, _options) => {
    return selected.map(({ label }) => label).join(",");
  };

  const localisedStrings = {
    allItemsAreSelected: "All items are selected.",
    clearSearch: "Clear Search",
    clearSelected: "Clear Selected",
    noOptions: "No options",
    search: "Search",
    selectAll: "Select All",
    selectAllFiltered: "Select All (Filtered)",
    selectSomeItems: intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Please select an option" }),
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        {files.map((file) => (
          <div key={file.path} style={{ fontSize: "12px" }}>
            {file.path} - {file.size} bytes
            <button className="btn btn-sm btn-primary ml-2 mb-2" onClick={() => removeFile(file)}>
              Remove
            </button>
          </div>
        ))}
      </CardHeader>
      <CardBody>
        <Row>
          <Col>
            <FormGroup row className="my-0">
              <Label sm={6}>
                <FormattedMessage id="app.name" defaultMessage="Name" />
              </Label>
              <Col sm={6}>
                <input
                  type="text"
                  className={`form-control ${name == "" ? "is-invalid" : ""}`}
                  id="name"
                  onKeyUp={(e) => updateFileDetails("NA", "name", e.target.value)}
                  maxLength={100}
                />
              </Col>
            </FormGroup>
            <FormGroup row className="my-0">
              <Label sm={6}>
                <FormattedMessage id="datos_placa.company" defaultMessage="Company" />
              </Label>
              <Col sm={6}>
                <Input type="select" value={selectedCompany} onChange={(e) => updateFileDetails("NA", "selectedCompany", parseInt(e.target.value))}>
                  <option value="">{intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Please select an option" })}</option>
                  {companies
                    .filter((a) => a.key !== -1)
                    .map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.key === -1 ? "Global" : a.value}
                      </option>
                    ))}
                </Input>
              </Col>
            </FormGroup>
            <FormGroup row className="my-0">
              <Label sm={6}>
                <FormattedMessage id="app.series" defaultMessage="Series" />
              </Label>
              <Col sm={6}>
                {/* <Input type="select" value={selectedSeries} onChange={(e) => updateFileDetails(file.path, "selectedSeries", e.target.value)}>
                  <option value="">{intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Please select an option" })}</option>
                  {existingSeries.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.value}
                    </option>
                  ))}
                </Input> */}
                <MultiSelect
                  valueRenderer={customValueRenderer}
                  options={existingSeries}
                  value={selectedMultiSeries}
                  hasSelectAll={false}
                  onChange={setSelectedMultiSeries}
                  labelledBy="Select"
                  overrideStrings={localisedStrings}
                />
              </Col>
            </FormGroup>
            <FormGroup row className="my-0">
              <Label sm={6}>
                <FormattedMessage id="certificates.language" defaultMessage="Language" />
              </Label>
              <Col sm={6}>
                <Input type="select" value={selectedLanguage} onChange={(e) => updateFileDetails("NA", "selectedLanguage", e.target.value)}>
                  <option value="">{intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Please select an option" })}</option>
                  {languages.map((l) => (
                    <option key={l.idIdioma} value={l.idIdioma}>
                      {l.idioma}
                    </option>
                  ))}
                </Input>
              </Col>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="form-group row mb-0 pb-0">
              <label htmlFor="name" className="col-sm-2 col-form-label">
                <FormattedMessage id="certificates.start_date" defaultMessage="Start date" />
              </label>
              <div className="col-sm-2">
                <input type="date" id="dateInput" value={startDate} className="form-control" onChange={(e) => updateFileDetails("NA", "startDate", e.target.value)} />
              </div>
              <label htmlFor="name" className="col-sm-2 col-form-label">
                <FormattedMessage id="certificates.end_date" defaultMessage="End date" />
              </label>
              <div className="col-sm-2">
                <input
                  type="date"
                  id="dateInput"
                  value={endDate}
                  className="form-control"
                  disabled={noEndDate}
                  onChange={(e) => updateFileDetails("NA", "endDate", e.target.value)}
                />
              </div>
              <div className="col-sm-2" style={{ display: "flex", alignItems: "center" }}>
                <div className="form-check">
                  {/* <input
                    className="form-check-input"
                    type="checkbox"
                    id={`no-expiry-${fileIndex}`}
                    checked={noEndDate}
                    onChange={(e) => updateFileDetails(file.path, "noEndDate", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`no-expiry-${fileIndex}`}>
                    <FormattedMessage id="certificates.no_expiry" defaultMessage="No expiration date" />
                  </label> */}
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`no-expiry`}
                    checked={noEndDate}
                    onChange={(e) => updateFileDetails("NA", "noEndDate", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`no-expiry`}>
                    <FormattedMessage id="certificates.no_expiry" defaultMessage="No expiration date" />
                  </label>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}

export default CertificatePdfUpload;
